/**
 * The endpoints a developer's own credential reaches.
 *
 * `/api/v1/seats/**` and nothing else: this application has no scope over the query API and must not -
 * the decision log names every author in the estate. The session itself lives in
 * `developerSession.js` beside it; see there for why.
 */

import {
   clearDeveloper, getDeveloper, isDeveloperSignedIn, onDeveloperChange, setDeveloper
} from './developerSession.js';

export {
   clearDeveloper, getDeveloper, isDeveloperSignedIn, onDeveloperChange, setDeveloper
};

/* ------------------------------------------------------------------ *
 * The endpoints
 * ------------------------------------------------------------------ */

export class SeatApiError extends Error
{
   constructor(status, message, details = null)
   {
      super(message);
      this.status = status;

      // The parsed body, for a caller that acts on more than the sentence (a 409's `title` and `invitedEmail`).
      this.details = details;
   }
}

async function send(path, { method = 'GET', body, auth = false, signal } = {})
{
   const headers = {};

   if (body !== undefined)
   {
      headers['content-type'] = 'application/json';
   }

   if (auth)
   {
      const token = getDeveloper()?.token;

      if (!token)
      {
         throw new SeatApiError(401, 'You are not signed in.');
      }

      headers.authorization = `Bearer ${token}`;
   }

   const response = await fetch(path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal
   });

   if (response.status === 401 && auth)
   {
      // The server disagrees with what is stored - revoked, expired, or a deployment that lost it. Drop
      // it here so the shell redraws as signed out instead of retrying against a credential that is gone.
      clearDeveloper();
   }

   const text = await response.text();
   let parsed = null;
   try
   {
      parsed = text ? JSON.parse(text) : null;
   }
   catch
   {
      throw new SeatApiError(response.status, 'The service could not complete the request. Please try again.');
   }

   if (!response.ok)
   {
      throw new SeatApiError(response.status, parsed?.detail ?? parsed?.title ?? parsed?.error ?? 'That did not work.', parsed);
   }

   return parsed;
}

/** How a visitor may sign in here. A public fact. */
export function loginOptions(signal)
{
   return send('/api/v1/seats/login/options', { signal });
}

/**
 * Where the browser goes to sign in with a provider: a page load, not a fetch, because the provider's
 * page is where it ends up. With `code`, the device code being signed in for a client; with `next`,
 * where a browser sign-in lands afterwards; with `chooser`, the account chooser even for an identity with
 * one account ("Use another account").
 */
export function providerStartUrl(provider, { code, next, chooser } = {})
{
   const query = new URLSearchParams();

   if (code)
   {
      query.set('code', code);
   }

   if (chooser)
   {
      query.set('chooser', 'true');
   }

   if (next)
   {
      query.set('next', next);
   }

   const suffix = query.toString();

   return `/api/v1/seats/login/${encodeURIComponent(provider)}/start${suffix ? `?${suffix}` : ''}`;
}

/**
 * An open device code, for the team step: `{ clientName, createdAt, expiresAt, preselectedAccountId, teams }`,
 * the teams being the signed-in account's own (personal first, then by name; `role` personal, owner or member,
 * and `seatActive`). Needs the session. 404 for a code that is unknown, expired or already completed.
 */
export function deviceInfo(userCode, signal)
{
   return send(`/api/v1/seats/login/device/${encodeURIComponent(userCode)}`, { auth: true, signal });
}

/** Signs the client that showed the code in for one of those teams. 204; 403 `not_in_team`; 404 once it is not open. */
export function completeDevice(userCode, accountId, signal)
{
   return send('/api/v1/seats/login/device/complete', { method: 'POST', body: { userCode, accountId }, auth: true, signal });
}

/** Spends the single-use code the provider callback handed the browser, for the session. */
export function exchangeLogin(token, signal)
{
   return send('/api/v1/seats/login/exchange?clientName=Browser', { method: 'POST', body: { token }, signal });
}

/** Mails a sign-in link and returns the code to poll with. Same shape as a device flow. */
export function startLogin(email, signal)
{
   return send('/api/v1/seats/login', { method: 'POST', body: { email }, signal });
}

/**
 * Where the flow stands.
 *
 * `clientName` is what the person sees on their own sessions list, so it says what is signed in rather
 * than repeating who they are.
 */
export function pollLogin(deviceCode, signal)
{
   const query = new URLSearchParams({ deviceCode, clientName: 'Browser' });

   return send(`/api/v1/seats/login/poll?${query}`, { signal });
}

/**
 * What the browser calls when somebody opens the link and types the code the client showed. Returns no
 * credential - see the endpoint. A wrong code answers 400 with the title `code_mismatch` and leaves the
 * link open.
 */
export function completeLogin(token, code, signal)
{
   return send('/api/v1/seats/login/complete', { method: 'POST', body: { token, code }, signal });
}

/**
 * Spends an impersonation code for the session it stands for.
 *
 * Anonymous, like the sign-in calls beside it: the caller has no credential yet, and acquiring one is
 * what this is for.
 */
export function redeemImpersonation(code, signal)
{
   return send('/api/v1/seats/impersonation', { method: 'POST', body: { token: code }, signal });
}

/**
 * Accepts a seat invitation, for the person signed in.
 *
 * A session is required. An account is its one e-mail, so only the account with the invited address accepts:
 * any other answers 409 with the title `other_account`, `invitedEmail` and `identity` (the provider account this
 * session would link, `{ provider, issuer, subject }`, or null), and the invitation stays open.
 * With `asInvitedAddress` the invitation mail is the proof of the address - a free one becomes a new account,
 * linked to this session's identity if it has one (409 `email_taken` otherwise). 401 with no session, 403 for a
 * borrowed view, 400 for a spent or expired link.
 */
export function acceptInvite(token, { asInvitedAddress = false } = {}, signal)
{
   return send('/api/v1/seats/invites/accept', {
      method: 'POST',
      body: asInvitedAddress ? { token, asInvitedAddress: true } : { token },
      auth: true,
      signal
   });
}

/* ------------------------------------------------------------------ *
 * The account chooser, and adding accounts and identities
 * ------------------------------------------------------------------ */

/**
 * What a provider sign-in can choose among: linked and parked accounts, candidate addresses. The ticket
 * is the callback's; 400 once it is spent or expired. Anonymous: the ticket is the credential.
 */
export function loginChoices(ticket, signal)
{
   return send(`/api/v1/seats/login/choose?ticket=${encodeURIComponent(ticket)}`, { signal });
}

/** `{ ticket, principalId }` or `{ ticket, address }`. Answers `{ exchange, next }`; spends the ticket. */
export function chooseLogin(body, signal)
{
   return send('/api/v1/seats/login/choose', { method: 'POST', body, signal });
}

/**
 * Mails a link that adds an account for `email` (or gives a parked account its first one) and answers the
 * code to poll with. From the chooser it carries the ticket; from a signed-in browser it has none and the
 * session is the proof.
 */
export function linkAccount({ ticket, email, principalId }, signal)
{
   return send('/api/v1/seats/login/link', {
      method: 'POST',
      body: { ticket, email, principalId },
      auth: !ticket,
      signal
   });
}

/** The accounts this session can switch to: `[{ principalId, email, current }]`. */
export function myAccounts(signal)
{
   return send('/api/v1/seats/me/accounts', { auth: true, signal });
}

/** Answers `{ exchange }`, a 60-second code for `exchangeLogin`. Nothing is revoked. */
export function switchAccount(principalId, signal)
{
   return send('/api/v1/seats/me/accounts/switch', { method: 'POST', body: { principalId }, auth: true, signal });
}

/** Answers `{ url }`: the provider page to send the browser to. The callback links the identity and lands on /profile. */
export function startIdentityLink(provider, signal)
{
   return send(`/api/v1/seats/me/identities/${encodeURIComponent(provider)}/link`, { method: 'POST', auth: true, signal });
}

/**
 * What a `linkTicket` would link, without spending it: `{ provider, issuer, subject, email }`. Only the session
 * that started the flow may read it (403 otherwise; 400 once spent or expired).
 */
export function pendingIdentityLink(ticket, signal)
{
   return send(`/api/v1/seats/me/identities/pending?ticket=${encodeURIComponent(ticket)}`, { auth: true, signal });
}

/**
 * Links the identity the provider callback returned, with the `linkTicket` it redirected to /profile with,
 * once the person has clicked Link. The service links only when this is the very session that started the
 * flow (403 otherwise); the ticket is single-use (400 once spent or expired).
 */
export function confirmIdentityLink(ticket, signal)
{
   return send('/api/v1/seats/me/identities/confirm', { method: 'POST', body: { ticket }, auth: true, signal });
}

/** Unlinks one identity of this account. The last one may go: the mail link still signs in. */
export function unlinkIdentity(identityId, signal)
{
   return send(`/api/v1/seats/me/identities/${encodeURIComponent(identityId)}`, { method: 'DELETE', auth: true, signal });
}

/** Everything this person can be told about themselves, in one request. */
export function overview(signal)
{
   return send('/api/v1/seats/me/overview', { auth: true, signal });
}

/** Releases the personal lock, so the next private repository becomes the one. Whenever they like. */
export function releaseLock(signal)
{
   return send('/api/v1/seats/me/lock', { method: 'DELETE', auth: true, signal });
}

/**
 * Ends the session this application holds, on the service. The caller forgets it locally afterwards,
 * whatever this answered: a sign-out that cannot reach the service is still a sign-out here.
 */
export function signOut(signal)
{
   return send('/api/v1/seats/me/session', { method: 'DELETE', auth: true, signal });
}

/**
 * The signed-in person's own runs over the last `days` days. `available: false` where telemetry is
 * off or unreachable, which the dashboard answers by showing nothing.
 */
export function usage(days, signal)
{
   return send(`/api/v1/seats/me/usage?days=${encodeURIComponent(days)}`, { auth: true, signal });
}

export function revokeSession(sessionId, signal)
{
   return send(`/api/v1/seats/me/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
      auth: true,
      signal
   });
}

/* ------------------------------------------------------------------ *
 * The accounts an owner runs
 * ------------------------------------------------------------------ */

function accountUrl(accountId, rest = '')
{
   return `/api/v1/seats/accounts/${encodeURIComponent(accountId)}${rest}`;
}

/** Creates a team. The caller owns it; it has no seats until some are bought. 400 for an empty name. */
export function createAccount(name, signal)
{
   return send('/api/v1/seats/accounts', { method: 'POST', body: { name }, auth: true, signal });
}

/** Everything an owner is shown about one account, in one request. 404 for one they do not own. */
export function accountOverview(accountId, signal)
{
   return send(accountUrl(accountId), { auth: true, signal });
}

/** Renames the team. */
export function updateAccount(accountId, body, signal)
{
   return send(accountUrl(accountId), { method: 'PATCH', body, auth: true, signal });
}

/**
 * Invites an address to a seat. The seat is held for them from now and the link is mailed; 400 with a
 * sentence when every seat is taken.
 */
export function inviteMember(accountId, email, signal)
{
   return send(accountUrl(accountId, '/invites'), { method: 'POST', body: { email }, auth: true, signal });
}

/** The owner takes a seat for themselves, without an invitation. 400 when they hold one or none is free. */
export function takeSeat(accountId, signal)
{
   return send(accountUrl(accountId, '/seats/me'), { method: 'POST', auth: true, signal });
}

export function withdrawInvite(accountId, inviteId, signal)
{
   return send(accountUrl(accountId, `/invites/${encodeURIComponent(inviteId)}`), { method: 'DELETE', auth: true, signal });
}

export function removeMember(accountId, seatId, signal)
{
   return send(accountUrl(accountId, `/seats/${encodeURIComponent(seatId)}`), { method: 'DELETE', auth: true, signal });
}

export function addOwner(accountId, email, signal)
{
   return send(accountUrl(accountId, '/owners'), { method: 'POST', body: { email }, auth: true, signal });
}

export function removeOwner(accountId, principalId, signal)
{
   return send(accountUrl(accountId, `/owners/${encodeURIComponent(principalId)}`), { method: 'DELETE', auth: true, signal });
}

/** Mints a CI credential. The plaintext is in the answer and nowhere else, ever. */
export function issueProjectToken(accountId, body, signal)
{
   return send(accountUrl(accountId, '/project-tokens'), { method: 'POST', body, auth: true, signal });
}

export function revokeProjectToken(accountId, tokenId, signal)
{
   return send(accountUrl(accountId, `/project-tokens/${encodeURIComponent(tokenId)}`), { method: 'DELETE', auth: true, signal });
}

/** What the billing page needs to open a checkout, or `configured: false` where seats are not sold. */
export function checkout(accountId, signal)
{
   return send(accountUrl(accountId, '/checkout'), { auth: true, signal });
}

/** Starts or resumes the team's server-owned transaction. */
export function startCheckout(accountId, quantity, signal)
{
   return send(accountUrl(accountId, '/checkout'), { method: 'POST', body: { quantity }, auth: true, signal });
}

/** Owner-only invoice and activity history. */
export function billingRead(accountId, resource, signal)
{
   return send(accountUrl(accountId, `/billing/${resource}`), { auth: true, signal });
}

/** Owner-only synchronization, preview, seat change, and cancellation. */
export function billingAction(accountId, action, body, signal)
{
   return send(accountUrl(accountId, `/billing/${action}`), { method: 'POST', body, auth: true, signal });
}
